import React from 'react'
import { graphql } from 'gatsby'
import { Buttons, LinkButton } from '../../components/button'
import { Tabs, Tab, TabList, TabPanel } from '../../components/tabs'
import { InstructionsVideo } from '../../components/instructions-video'
import { Image } from '../../components/image'
import { useBrand } from '../../components/brand'
import { useSnippets } from '../../components/snippet'
import { VideoList } from '../../components/videolist'
import {
  Container,
  Intro,
  Subtitle,
  Text,
  Title,
  Section,
} from '../../components/page'

import * as styles from './how-it-works.module.scss'
import { VideoCard } from '../../components/videocard'

export type HowItWorksProps = Queries.HowItWorksFragment

export const HowItWorks = ({
  title,
  text,
  youtubeLinks,
  buttons,
  tabs,
}: HowItWorksProps) => {
  const brand = useBrand()
  const s = useSnippets()

  return (
    <Section className={styles.section}>
      <Container>
        <Title center>{title}</Title>
        {youtubeLinks?.length && (
          <VideoList>
            {youtubeLinks?.map((item) => (
              <InstructionsVideo src={item?.url as string} key={item?.id}>
                <VideoCard url={item?.url!} title={item?.title!} />
              </InstructionsVideo>
            ))}
          </VideoList>
        )}
        <Intro center>{text?.text}</Intro>
        <Buttons className={styles.topbuttons} center>
          {buttons?.map((item, index) => (
            <LinkButton
              key={item?.id}
              link={item}
              color={index === 0 ? 'primary' : 'secondary'}
              variant={index === 0 ? 'border' : null}
              size="lg"
            >
              {item?.title}
            </LinkButton>
          ))}
        </Buttons>
        <Tabs className={styles.tabs}>
          <TabList>
            {tabs?.map((tab) => (
              <Tab key={tab?.id}>{tab?.title}</Tab>
            ))}
          </TabList>
          {tabs?.map((tab) => (
            <TabPanel key={tab?.id}>
              <div className={styles.tab}>
                <ol className={styles.list}>
                  {tab?.items?.map((item) => (
                    <li key={item?.id} className={styles.item}>
                      <div className={styles.text}>
                        <Subtitle>{item?.title}</Subtitle>
                        <Text>{item?.text?.text}</Text>
                      </div>
                      <div className={styles.image}>
                        <Image
                          image={item?.image}
                          alt={item?.image?.title!}
                          objectFit="contain"
                          loading="eager"
                        />
                      </div>
                    </li>
                  ))}
                </ol>
                <Buttons className={styles.buttons}>
                  {tab?.buttons?.map((item, index) => (
                    <LinkButton
                      key={item?.id}
                      link={item}
                      color={index === 0 ? 'primary' : 'secondary'}
                      variant={index === 0 ? 'border' : null}
                      size="lg"
                    >
                      {item?.title}
                    </LinkButton>
                  ))}
                </Buttons>
              </div>
            </TabPanel>
          ))}
        </Tabs>
      </Container>
    </Section>
  )
}

export const HowItWorksFragments = graphql`
  fragment HowItWorksImage on ContentfulAsset {
    __typename
    id
    title
    gatsbyImageData(width: 563)
    file {
      url
      details {
        image {
          width
          height
        }
      }
    }
  }
  fragment HowItWorksTabItem on ContentfulHowItWorksTabItem {
    __typename
    id
    title
    text {
      text
    }
    image {
      ...HowItWorksImage
    }
  }
  fragment HowItWorksTab on ContentfulHowItWorksTab {
    __typename
    id
    title
    items {
      ...HowItWorksTabItem
    }
    buttons {
      ...Link
    }
  }
  fragment HowItWorks on ContentfulHowItWorks {
    __typename
    id
    title
    text {
      text
    }
    youtubeLinks {
      ...Link
    }
    buttons {
      ...Link
    }
    tabs {
      ...HowItWorksTab
    }
  }
`
